
export interface Template {
  id: string;
  slug: string;
  title: string;
  description: string;
  category: string;
  icon: string;
  previewImage: string;
  galleryImages: string[];
  gradient: string;
  examples: string[];
  prompt: string;
  tips: string[];
  metaTitle: string;
  metaDescription: string;
  keywords: string[];
}


// All templates consolidated into one file
// Total: 112 templates across multiple categories
export const templates: Template[] = [
  // === Base Templates (10) ===
  // Web Design Templates
  {
    id: 'website-illustration',
    slug: 'website-illustration',
    title: 'Website Illustration',
    description:
      'Create stunning hero images, feature graphics, and decorative elements for modern websites',
    category: 'Web Design',
    icon: '🌐',
    previewImage: `https://via.placeholder.com/400x300/667eea/ffffff?text=Website+Illustration`,
    galleryImages: [],
    gradient: 'from-blue-500 to-purple-600',
    examples: [
      'Modern tech startup hero illustration with abstract shapes',
      'E-commerce product showcase graphics',
      'SaaS dashboard feature illustrations',
      'Landing page background patterns',
    ],
    prompt: 'Create a modern website illustration',
    tips: [
      'Use vibrant gradients for modern appeal',
      'Keep designs scalable and responsive',
      'Consider dark and light mode variations',
      'Optimize for web performance',
    ],
    metaTitle: 'Website Illustration Generator - Create Custom Web Graphics | SVG.new',
    metaDescription:
      'Generate professional website illustrations, hero images, and web graphics instantly with AI. Perfect for landing pages, blogs, and web applications.',
    keywords: [
      'website illustration',
      'web graphics',
      'hero image',
      'landing page design',
      'svg illustration',
    ],
  },
  {
    id: 'sticker-design',
    slug: 'sticker-design',
    title: 'Sticker Design',
    description: 'Design fun and expressive stickers for messaging apps, social media, and print',
    category: 'Digital Art',
    icon: '🎨',
    previewImage: `/images/templates/sticker-design/svg-sloth.svg`,
    galleryImages: [
      '/images/templates/sticker-design/svg-4d839b11.svg',
      '/images/templates/sticker-design/svg-49cdafd3.svg',
      '/images/templates/sticker-design/svg-4421926d.svg',
      '/images/templates/sticker-design/svg-4099a5f1.svg',
    ],
    gradient: 'from-pink-500 to-yellow-500',
    examples: [
      'Cute animal character sticker',
      'Emoji-style reaction sticker',
      'Motivational quote sticker',
      'Brand mascot sticker',
    ],
    prompt: 'Design a fun sticker',
    tips: [
      'Use bold outlines for clarity',
      'Keep designs simple and readable',
      'Add personality with expressions',
    ],
    metaTitle: 'Sticker Design Generator - Create Custom Stickers | SVG.new',
    metaDescription:
      'Design unique stickers for WhatsApp, Telegram, and social media. Generate cute characters, emojis, and custom sticker packs with AI.',
    keywords: [
      'sticker design',
      'custom stickers',
      'emoji stickers',
      'whatsapp stickers',
      'digital stickers',
    ],
  },
  {
    id: 'tattoo-design',
    slug: 'tattoo-design',
    title: 'Tattoo Design',
    description: 'Create unique tattoo designs from minimalist line art to detailed compositions',
    category: 'Art & Design',
    icon: '🖤',
    previewImage: `/images/templates/tattoo-design/butterfly.svg`,
    galleryImages: [
      '/images/templates/tattoo-design/tribal.svg',
      '/images/templates/tattoo-design/wolf.svg',
      '/images/templates/tattoo-design/rose.svg',
      '/images/templates/tattoo-design/donut.svg',
    ],
    gradient: 'from-gray-800 to-black',
    examples: [
      'Tribal tattoo design',
      'Traditional style rose and dagger',
      'Mandala and sacred geometry pattern',
      'Nature-inspired sleeve design',
    ],
    prompt: 'Create a tattoo design',
    tips: [
      'Focus on clean lines and contrast',
      'Consider placement and body curves',
      'Design with black ink in mind',
      'Think about scalability',
    ],
    metaTitle: 'Tattoo Design Generator - Create Custom Tattoo Art | SVG.new',
    metaDescription:
      'Generate unique tattoo designs with AI. From minimalist to traditional styles, create personalized tattoo art for your next ink.',
    keywords: [
      'tattoo design',
      'custom tattoo',
      'tattoo art',
      'tattoo generator',
      'minimalist tattoo',
    ],
  },
  {
    id: 'icon-set',
    slug: 'icon-set',
    title: 'Icon Set',
    description: 'Build consistent icon sets for apps, websites, and user interfaces',
    category: 'UI/UX',
    icon: '📱',
    previewImage: `https://via.placeholder.com/400x300/48bb78/ffffff?text=Icon+Set`,
    galleryImages: [],
    gradient: 'from-green-500 to-teal-600',
    examples: [
      'Mobile app navigation icons',
      'E-commerce action icons',
      'Social media interface icons',
      'File type and document icons',
    ],
    prompt: 'Create a set of UI icons',
    tips: [
      'Maintain consistent stroke width',
      'Use a unified grid system',
      'Design for multiple sizes',
      'Keep style cohesive across set',
    ],
    metaTitle: 'Icon Set Generator - Create Custom UI Icons | SVG.new',
    metaDescription:
      'Generate professional icon sets for apps and websites. Create consistent, scalable UI icons perfect for modern interfaces.',
    keywords: ['icon set', 'ui icons', 'app icons', 'interface icons', 'svg icons'],
  },
  {
    id: 'tshirt-design',
    slug: 'tshirt-design',
    title: 'T-Shirt Design',
    description: 'Design eye-catching graphics for t-shirts, hoodies, and apparel',
    category: 'Fashion',
    icon: '👕',
    previewImage: `https://via.placeholder.com/400x300/f56565/ffffff?text=T-Shirt+Design`,
    galleryImages: [],
    gradient: 'from-red-500 to-orange-600',
    examples: [
      'Vintage band t-shirt graphics',
      'Minimalist typography designs',
      'Pop culture reference shirts',
      'Abstract artistic prints',
    ],
    prompt: 'Design a t-shirt graphic',
    tips: [
      'Design for different shirt colors',
      'Consider print limitations',
      'Create high-contrast designs',
      'Think about placement and size',
    ],
    metaTitle: 'T-Shirt Design Generator - Create Custom Apparel Graphics | SVG.new',
    metaDescription:
      'Design unique t-shirt graphics with AI. Generate print-ready designs for custom apparel, merchandise, and fashion brands.',
    keywords: [
      'tshirt design',
      'apparel graphics',
      'custom tshirt',
      'print design',
      'merchandise design',
    ],
  },
  {
    id: 'logo-design',
    slug: 'logo-design',
    title: 'Logo Design',
    description: 'Create professional logos and brand marks for businesses and projects',
    category: 'Branding',
    icon: '✨',
    previewImage: `/images/templates/logo-design/Acme.ai Logo-4227b3c4.svg`,
    galleryImages: [
      '/images/templates/logo-design/Acme AI Logo-48a2a03c.svg',
      '/images/templates/logo-design/Modern Tech Startup Logo-43f095c0.svg',
      '/images/templates/logo-design/Acme.ai Logo-4f979a37.svg',
      '/images/templates/logo-design/Acme.ai Logo-43d1b4a5.svg',
    ],
    gradient: 'from-purple-600 to-indigo-600',
    examples: [
      'Modern tech startup logo',
      'Minimalist wordmark',
      'Abstract geometric symbol',
      'Vintage badge design',
    ],
    prompt: 'Design a professional logo',
    tips: [
      'Keep it simple and memorable',
      'Ensure scalability',
      'Design in black and white first',
      'Consider different use cases',
    ],
    metaTitle: 'Logo Design Generator - Create Professional Logos | SVG.new',
    metaDescription:
      'Generate professional logos and brand marks with AI. Perfect for startups, businesses, and personal brands.',
    keywords: ['logo design', 'brand logo', 'business logo', 'logo generator', 'brand identity'],
  },
  // {
  //   id: 'pattern-design',
  //   slug: 'pattern-design',
  //   title: 'Pattern Design',
  //   description: 'Generate seamless patterns for backgrounds, textiles, and decorative use',
  //   category: 'Decorative',
  //   icon: '🔷',
  //   previewImage: `https://via.placeholder.com/400x300/4299e1/ffffff?text=Pattern+Design`,
  //   galleryImages: getPlaceholderImages('pattern-design'),
  //   gradient: 'from-cyan-500 to-blue-600',
  //   examples: [
  //     'Geometric repeating pattern',
  //     'Floral wallpaper design',
  //     'Abstract seamless texture',
  //     'Ethnic and cultural motif',
  //   ],
  //   prompt: 'Create a seamless pattern',
  //   tips: [
  //     'Ensure perfect tiling',
  //     'Balance complexity and clarity',
  //     'Consider scale and application',
  //     'Test pattern repetition',
  //   ],
  //   metaTitle: 'Pattern Design Generator - Create Seamless Patterns | SVG.new',
  //   metaDescription:
  //     'Generate beautiful seamless patterns for backgrounds, textiles, and wallpapers. Create repeating designs with AI.',
  //   keywords: [
  //     'pattern design',
  //     'seamless pattern',
  //     'textile design',
  //     'wallpaper pattern',
  //     'repeating pattern',
  //   ],
  // },
  {
    id: 'badge-design',
    slug: 'badge-design',
    title: 'Badge & Emblem',
    description: 'Design badges, emblems, and achievement graphics for games and apps',
    category: 'Gaming',
    icon: '🏆',
    previewImage: `/images/templates/badge-design/Game Achievement Badge-4060ad97.svg`,
    galleryImages: [
      '/images/templates/badge-design/Vintage Badge Design-46a6b926.svg',
      '/images/templates/badge-design/Sports Team Emblem-4a58ab51.svg',
      '/images/templates/badge-design/Gaming Achievement Badge-40fca5be.svg',
      '/images/templates/badge-design/Certification Badge-4e6eab47.svg',
    ],
    gradient: 'from-yellow-500 to-amber-600',
    examples: [
      'Achievement badge for games',
      'Certification emblem',
      'Vintage style badge',
      'Sports team emblem',
    ],
    prompt: 'Design a badge or emblem',
    tips: [
      'Use strong shapes and borders',
      'Include relevant symbols',
      'Consider metallic effects',
      'Make text readable at small sizes',
    ],
    metaTitle: 'Badge Design Generator - Create Custom Badges & Emblems | SVG.new',
    metaDescription:
      'Design professional badges, emblems, and achievement graphics. Perfect for games, certifications, and awards.',
    keywords: [
      'badge design',
      'emblem design',
      'achievement badge',
      'game badges',
      'certification badge',
    ],
  },
  {
    id: 'character-design',
    slug: 'character-design',
    title: 'Character Design',
    description: 'Create unique characters and mascots for brands, games, and stories',
    category: 'Illustration',
    icon: '🦸',
    previewImage: `/images/templates/character-design/svg-47889432.svg`,
    galleryImages: [
      '/images/templates/character-design/svg-4ae3a78c.svg',
      '/images/templates/character-design/svg-4d7398c2.svg',
      '/images/templates/character-design/svg-4253844a.svg',
      '/images/templates/character-design/svg-48408412.svg',
    ],
    gradient: 'from-violet-500 to-purple-600',
    examples: [
      'Cute mascot character',
      'Game character sprite',
      'Brand ambassador',
      'Cartoon avatar',
    ],
    prompt: 'Design a character',
    tips: [
      'Define personality through design',
      'Use distinctive silhouettes',
      'Consider animation potential',
      'Create memorable features',
    ],
    metaTitle: 'Character Design Generator - Create Custom Characters & Mascots | SVG.new',
    metaDescription:
      'Design unique characters and mascots with AI. Perfect for brands, games, and digital storytelling.',
    keywords: [
      'character design',
      'mascot design',
      'game character',
      'brand mascot',
      'cartoon character',
    ],
  },
  {
    id: 'infographic-elements',
    slug: 'infographic-elements',
    title: 'Infographic Elements',
    description: 'Create data visualization elements and infographic components',
    category: 'Data Viz',
    icon: '📊',
    previewImage: `/images/templates/infographic-elements/svg-49cab645.svg`,
    galleryImages: [
      '/images/templates/infographic-elements/svg-45cf9efd.svg',
      '/images/templates/infographic-elements/svg-4106a7a8.svg',
      '/images/templates/infographic-elements/svg-4c6ea54f.svg',
      '/images/templates/infographic-elements/svg-4ae79a98.svg',
    ],
    gradient: 'from-emerald-500 to-green-600',
    examples: [
      'Chart and graph illustration',
      'Process flow diagram',
      'Timeline graphic',
      'Statistical icon',
    ],
    prompt: 'Create infographic elements',
    tips: [
      'Keep data clear and accurate',
      'Use color to guide attention',
      'Simplify complex information',
      'Maintain visual hierarchy',
    ],
    metaTitle: 'Infographic Elements Generator - Create Data Visualizations | SVG.new',
    metaDescription:
      'Generate professional infographic elements and data visualizations. Perfect for presentations, reports, and educational content.',
    keywords: [
      'infographic design',
      'data visualization',
      'chart design',
      'diagram illustration',
      'info graphics',
    ],
  },

  // === Extended Templates Set 1 (20) ===
  // Additional templates to expand each category
  // More Web Design Templates
  {
    id: 'landing-hero',
    slug: 'landing-hero',
    title: 'Landing Page Hero',
    description: 'Eye-catching hero sections with call-to-action elements for landing pages',
    category: 'Web Design',
    icon: '🚀',
    previewImage: '/images/templates/landing-hero/svg-4bbaab87.svg',
    galleryImages: [
      '/images/templates/landing-hero/svg-16.svg',
      '/images/templates/landing-hero/svg-17.svg',
      '/images/templates/landing-hero/svg-43eb8496.svg',
      '/images/templates/landing-hero/svg-49f0b45b.svg',
    ],
    gradient: 'from-blue-500 to-purple-600',
    examples: [
      'SaaS product hero section with floating dashboard preview',
      'App launch hero section with phone mockups',
      'Service business hero section with trust badges',
      'Startup hero section with animated elements',
    ],
    prompt: 'Create a landing page hero section',
    tips: [
      'Focus on clear value proposition',
      'Include compelling CTA buttons',
      'Use contrast for important elements',
      'Keep mobile responsiveness in mind',
    ],
    metaTitle: 'Landing Page Hero Generator - Create Stunning Hero Sections | SVG.new',
    metaDescription:
      'Design professional landing page hero sections with AI. Perfect for conversions and engagement.',
    keywords: ['landing page', 'hero section', 'conversion', 'cta design'],
  },
  {
    id: 'blog-header',
    slug: 'blog-header',
    title: 'Blog Header Graphics',
    description: 'Engaging header images for blog posts and articles',
    category: 'Web Design',
    icon: '📝',
    previewImage: `https://via.placeholder.com/400x300/667eea/ffffff?text=Blog+Header`,
    galleryImages: [],
    gradient: 'from-indigo-500 to-blue-600',
    examples: [
      'Tech blog header with circuit patterns',
      'Lifestyle blog header with organic shapes',
      'Business blog header with professional graphics',
      'Creative blog header with artistic elements',
    ],
    prompt: 'Design a blog header graphic',
    tips: [
      "Match your blog's theme and tone",
      'Leave space for title overlay',
      'Use consistent style across posts',
      'Consider social media crop ratios',
    ],
    metaTitle: 'Blog Header Graphics Generator - Create Engaging Blog Images | SVG.new',
    metaDescription:
      'Generate stunning blog header graphics with AI. Perfect for increasing engagement and shares.',
    keywords: ['blog header', 'blog graphics', 'article header', 'content design'],
  },
  {
    id: 'error-404',
    slug: 'error-404',
    title: '404 Error Page',
    description: 'Creative and friendly 404 error page illustrations',
    category: 'Web Design',
    icon: '🔍',
    previewImage: `/images/templates/error-404/svg-49239333.svg`,
    galleryImages: [
      '/images/templates/error-404/svg-4d3f8804.svg',
      '/images/templates/error-404/svg-41958bd7.svg',
      '/images/templates/error-404/svg-4522827f.svg',
      '/images/templates/error-404/svg-43beadc7.svg',
    ],
    gradient: 'from-purple-500 to-pink-600',
    examples: [
      'Lost in space themed 404',
      'Broken robot character 404',
      'Map with wrong turn illustration',
      'Cute monster ate the page design',
    ],
    prompt: 'Create a 404 error page illustration',
    tips: [
      'Keep it friendly and humorous',
      'Include helpful navigation options',
      'Match your brand personality',
      'Add search or home button',
    ],
    metaTitle: '404 Error Page Generator - Create Custom Error Pages | SVG.new',
    metaDescription:
      'Design creative 404 error page illustrations that turn errors into engaging experiences.',
    keywords: ['404 page', 'error page', 'not found', 'web error design'],
  },

  // More Digital Art Templates
  {
    id: 'emoji-pack',
    slug: 'emoji-pack',
    title: 'Custom Emoji Pack',
    description: 'Create unique emoji sets for Discord, Slack, and social platforms',
    category: 'Digital Art',
    icon: '😊',
    previewImage: `https://via.placeholder.com/400x300/f687b3/ffffff?text=Emoji+Pack`,
    galleryImages: [],
    gradient: 'from-yellow-400 to-pink-500',
    examples: [
      'Office reaction emojis for Slack',
      'Gaming emotes for Discord',
      'Food themed emoji collection',
      'Animal expressions emoji set',
    ],
    prompt: 'Design a custom emoji',
    tips: [
      'Keep designs simple and readable',
      'Test at small sizes (32x32px)',
      'Use bold colors and expressions',
      'Maintain consistent style across set',
    ],
    metaTitle: 'Custom Emoji Pack Generator - Create Unique Emojis | SVG.new',
    metaDescription: 'Design custom emoji packs for Discord, Slack, and social platforms with AI.',
    keywords: ['custom emoji', 'discord emotes', 'slack emoji', 'emoji design'],
  },
  {
    id: 'avatar-maker',
    slug: 'avatar-maker',
    title: 'Avatar Creator',
    description: 'Design unique avatars and profile pictures for social media',
    category: 'Digital Art',
    icon: '👤',
    previewImage: `/images/templates/avatar-maker/svg-45ef94c4.svg`,
    galleryImages: [
      '/images/templates/avatar-maker/svg-4f5983bd.svg',
      '/images/templates/avatar-maker/svg-428f9970.svg',
      '/images/templates/avatar-maker/svg-44349da5.svg',
      '/images/templates/avatar-maker/svg-44648936.svg',
    ],
    gradient: 'from-green-400 to-blue-500',
    examples: [
      'Minimalist geometric avatar',
      'Cartoon style self-portrait',
      'Professional headshot illustration',
      'Fantasy character avatar',
    ],
    prompt: 'Create a unique avatar',
    tips: [
      'Consider platform requirements',
      'Ensure good contrast',
      'Test in circular crop',
      'Keep details visible at small size',
    ],
    metaTitle: 'Avatar Creator - Design Custom Profile Pictures | SVG.new',
    metaDescription: 'Generate unique avatars and profile pictures for social media with AI.',
    keywords: ['avatar creator', 'profile picture', 'social media avatar', 'custom avatar'],
  },

  // More UI/UX Templates
  {
    id: 'mobile-icons',
    slug: 'mobile-icons',
    title: 'Mobile App Icons',
    description: 'Design app icons for iOS and Android applications',
    category: 'UI/UX',
    icon: '📲',
    previewImage: `https://via.placeholder.com/400x300/48bb78/ffffff?text=App+Icons`,
    galleryImages: [],
    gradient: 'from-green-500 to-teal-600',
    examples: [
      'Fitness app icon with activity rings',
      'Finance app icon with chart elements',
      'Social app icon with chat bubbles',
      'Game app icon with playful character',
    ],
    prompt: 'Design a mobile app icon',
    tips: [
      'Follow platform guidelines',
      'Test on different backgrounds',
      'Avoid text in icons',
      'Ensure scalability from 29px to 1024px',
    ],
    metaTitle: 'Mobile App Icon Generator - Create iOS & Android Icons | SVG.new',
    metaDescription: 'Design professional mobile app icons for iOS and Android with AI assistance.',
    keywords: ['app icon', 'mobile icon', 'ios icon', 'android icon'],
  },
  {
    id: 'dashboard-widgets',
    slug: 'dashboard-widgets',
    title: 'Dashboard Widgets',
    description: 'Create visual components for analytics dashboards',
    category: 'UI/UX',
    icon: '📈',
    previewImage: `https://via.placeholder.com/400x300/48bb78/ffffff?text=Dashboard`,
    galleryImages: [],
    gradient: 'from-blue-500 to-green-500',
    examples: [
      'Revenue growth chart widget',
      'User activity heatmap',
      'Progress indicator cards',
      'KPI metric displays',
    ],
    prompt: 'Create a dashboard widget',
    tips: [
      'Use clear data visualization',
      'Maintain visual hierarchy',
      'Consider real-time updates',
      'Ensure responsive scaling',
    ],
    metaTitle: 'Dashboard Widget Generator - Create Data Visualizations | SVG.new',
    metaDescription:
      'Design professional dashboard widgets and data visualization components with AI.',
    keywords: ['dashboard widgets', 'data visualization', 'analytics ui', 'chart design'],
  },

  // More Fashion Templates
  {
    id: 'hoodie-design',
    slug: 'hoodie-design',
    title: 'Hoodie Graphics',
    description: 'Design graphics for hoodies and sweatshirts',
    category: 'Fashion',
    icon: '🧥',
    previewImage: `https://via.placeholder.com/400x300/f56565/ffffff?text=Hoodie+Design`,
    galleryImages: [],
    gradient: 'from-red-500 to-pink-600',
    examples: [
      'Streetwear brand hoodie design',
      'University hoodie graphics',
      'Minimalist text-based design',
      'Japanese-inspired artwork',
    ],
    prompt: 'Design hoodie graphics',
    tips: [
      'Consider front and back placement',
      'Account for pocket placement',
      'Design for different hoodie colors',
      'Keep important elements centered',
    ],
    metaTitle: 'Hoodie Design Generator - Create Custom Sweatshirt Graphics | SVG.new',
    metaDescription: 'Design unique hoodie graphics and sweatshirt artwork with AI-powered tools.',
    keywords: ['hoodie design', 'sweatshirt graphics', 'streetwear design', 'apparel art'],
  },
  {
    id: 'hat-embroidery',
    slug: 'hat-embroidery',
    title: 'Hat Embroidery',
    description: 'Create embroidery designs for caps and hats',
    category: 'Fashion',
    icon: '🧢',
    previewImage: `https://via.placeholder.com/400x300/f56565/ffffff?text=Hat+Design`,
    galleryImages: [],
    gradient: 'from-orange-500 to-red-600',
    examples: [
      'Sports team cap logo',
      'Vintage baseball cap design',
      'Trucker hat graphics',
      'Dad hat minimalist text',
    ],
    prompt: 'Create hat embroidery design',
    tips: [
      'Keep designs simple for embroidery',
      'Limit color palette',
      'Consider stitch limitations',
      'Test on curved surfaces',
    ],
    metaTitle: 'Hat Embroidery Design Generator - Create Cap Graphics | SVG.new',
    metaDescription: 'Design custom embroidery patterns for hats and caps with AI assistance.',
    keywords: ['hat embroidery', 'cap design', 'embroidery pattern', 'headwear design'],
  },

  // More Branding Templates
  {
    id: 'business-card',
    slug: 'business-card',
    title: 'Business Card Design',
    description: 'Create professional business card layouts and designs',
    category: 'Branding',
    icon: '💼',
    previewImage: `https://via.placeholder.com/400x300/805ad5/ffffff?text=Business+Card`,
    galleryImages: [],
    gradient: 'from-purple-600 to-blue-600',
    examples: [
      'Minimalist corporate card',
      'Creative agency business card',
      'Personal brand card design',
      'Tech startup business card',
    ],
    prompt: 'Design a business card',
    tips: [
      'Include essential contact info',
      'Maintain brand consistency',
      'Consider print bleed areas',
      'Use readable font sizes',
    ],
    metaTitle: 'Business Card Design Generator - Create Professional Cards | SVG.new',
    metaDescription:
      'Design professional business cards with AI. Perfect for networking and brand identity.',
    keywords: ['business card', 'card design', 'professional card', 'brand identity'],
  },
  {
    id: 'letterhead',
    slug: 'letterhead',
    title: 'Letterhead Design',
    description: 'Design professional letterheads for business correspondence',
    category: 'Branding',
    icon: '📄',
    previewImage: `https://via.placeholder.com/400x300/805ad5/ffffff?text=Letterhead`,
    galleryImages: [],
    gradient: 'from-indigo-600 to-purple-600',
    examples: [
      'Corporate letterhead with watermark',
      'Law firm letterhead design',
      'Creative agency letterhead',
      'Non-profit organization letterhead',
    ],
    prompt: 'Create a letterhead design',
    tips: [
      'Keep design subtle and professional',
      'Leave ample white space for content',
      'Include all necessary company info',
      'Consider digital and print versions',
    ],
    metaTitle: 'Letterhead Design Generator - Create Business Stationery | SVG.new',
    metaDescription: 'Design professional letterheads for business correspondence with AI tools.',
    keywords: [
      'letterhead design',
      'business stationery',
      'corporate letterhead',
      'brand stationery',
    ],
  },

  // More Gaming Templates
  {
    id: 'game-ui',
    slug: 'game-ui',
    title: 'Game UI Elements',
    description: 'Design user interface elements for video games',
    category: 'Gaming',
    icon: '🎮',
    previewImage: `https://via.placeholder.com/400x300/ed8936/ffffff?text=Game+UI`,
    galleryImages: [],
    gradient: 'from-orange-500 to-red-600',
    examples: [
      'RPG inventory interface',
      'FPS HUD elements',
      'Mobile game buttons and controls',
      'Strategy game resource bars',
    ],
    prompt: 'Design game UI elements',
    tips: [
      'Ensure high visibility during gameplay',
      'Consider different screen sizes',
      'Use consistent visual language',
      'Test with actual gameplay footage',
    ],
    metaTitle: 'Game UI Generator - Create Video Game Interface Elements | SVG.new',
    metaDescription: 'Design professional game UI elements and HUD components with AI assistance.',
    keywords: ['game ui', 'game interface', 'hud design', 'video game ui'],
  },
  {
    id: 'twitch-overlay',
    slug: 'twitch-overlay',
    title: 'Stream Overlay',
    description: 'Create overlays and graphics for Twitch and YouTube streams',
    category: 'Gaming',
    icon: '📺',
    previewImage: `https://via.placeholder.com/400x300/ed8936/ffffff?text=Stream+Overlay`,
    galleryImages: [],
    gradient: 'from-purple-500 to-pink-600',
    examples: [
      'Gaming stream overlay with webcam frame',
      'Just Chatting overlay design',
      'Alert animations and notifications',
      'Stream starting/ending screens',
    ],
    prompt: 'Create stream overlay graphics',
    tips: [
      'Leave space for game content',
      'Ensure text is readable on stream',
      'Match your channel branding',
      'Consider different game aspects ratios',
    ],
    metaTitle: 'Stream Overlay Generator - Create Twitch & YouTube Graphics | SVG.new',
    metaDescription:
      'Design professional stream overlays and graphics for Twitch and YouTube with AI.',
    keywords: ['stream overlay', 'twitch graphics', 'youtube overlay', 'streaming design'],
  },

  // More Illustration Templates
  {
    id: 'book-cover',
    slug: 'book-cover',
    title: 'Book Cover Art',
    description: 'Design compelling book covers for print and digital publishing',
    category: 'Illustration',
    icon: '📚',
    previewImage: `/images/templates/book-cover/svg-412e8d7b.svg`,
    galleryImages: [
      '/images/templates/book-cover/svg-4ecf831a.svg',
      '/images/templates/book-cover/svg-4cb28111.svg',
      '/images/templates/book-cover/svg-45a4b164.svg',
      '/images/templates/book-cover/svg-450ea042.svg',
    ],
    gradient: 'from-violet-500 to-purple-600',
    examples: [
      'Fantasy novel cover with mystical elements',
      'Business book minimalist cover',
      "Children's book colorful illustration",
      'Thriller novel dark atmosphere cover',
    ],
    prompt: 'Design a book cover',
    tips: [
      'Make title clearly readable',
      'Consider thumbnail visibility',
      'Match genre expectations',
      'Leave space for author name and reviews',
    ],
    metaTitle: 'Book Cover Art Generator - Create Publishing Graphics | SVG.new',
    metaDescription: 'Design professional book covers for print and digital publishing with AI.',
    keywords: ['book cover', 'book design', 'cover art', 'publishing design'],
  },
  {
    id: 'poster-design',
    slug: 'poster-design',
    title: 'Poster Design',
    description: 'Create eye-catching posters for events, movies, and promotions',
    category: 'Illustration',
    icon: '🎬',
    previewImage: '/images/templates/poster-design/svg-4a1c94fd.svg',
    galleryImages: [
      '/images/templates/poster-design/svg-43a9ab6e.svg',
      '/images/templates/poster-design/svg-4e5eaddb.svg',
      '/images/templates/poster-design/svg-48a8b198.svg',
      '/images/templates/poster-design/svg-4544a0ca.svg',
    ],
    gradient: 'from-red-500 to-yellow-600',
    examples: [
      'Concert poster with band imagery',
      'Movie poster dramatic composition',
      'Event poster with date emphasis',
      'Art exhibition minimalist poster',
    ],
    prompt: 'Create a poster design',
    tips: [
      'Create strong visual hierarchy',
      'Ensure readability from distance',
      'Use compelling imagery',
      'Include all essential information',
    ],
    metaTitle: 'Poster Design Generator - Create Event & Promotional Posters | SVG.new',
    metaDescription:
      'Design stunning posters for events, movies, and promotions with AI-powered tools.',
    keywords: ['poster design', 'event poster', 'promotional poster', 'graphic design'],
  },

  // More Data Viz Templates
  {
    id: 'presentation-slides',
    slug: 'presentation-slides',
    title: 'Presentation Graphics',
    description: 'Create visual elements for PowerPoint and Keynote presentations',
    category: 'Data Viz',
    icon: '📊',
    previewImage: `/images/templates/presentation-slides/svg-40909537.svg`,
    galleryImages: [
      '/images/templates/presentation-slides/svg-422f9a0e.svg',
      '/images/templates/presentation-slides/svg-485c9c09.svg',
      '/images/templates/presentation-slides/svg-4faa8e15.svg',
      '/images/templates/presentation-slides/svg-4d2ea1c7.svg',
    ],
    gradient: 'from-teal-500 to-green-600',
    examples: [
      'Business process flow diagram',
      'Timeline infographic slide',
      'Comparison chart visualization',
      'Team organization chart',
    ],
    prompt: 'Create presentation graphics',
    tips: [
      'Keep slides uncluttered',
      'Use consistent color scheme',
      'Make text large enough to read',
      'Focus on one key point per slide',
    ],
    metaTitle: 'Presentation Graphics Generator - Create Slide Visuals | SVG.new',
    metaDescription:
      'Design professional presentation graphics and slide visuals with AI assistance.',
    keywords: ['presentation graphics', 'slide design', 'powerpoint graphics', 'keynote design'],
  },
  {
    id: 'report-charts',
    slug: 'report-charts',
    title: 'Report Charts',
    description: 'Design data visualizations for business reports and documents',
    category: 'Data Viz',
    icon: '📉',
    previewImage: `https://via.placeholder.com/400x300/38b2ac/ffffff?text=Report+Charts`,
    galleryImages: [],
    gradient: 'from-blue-500 to-teal-600',
    examples: [
      'Annual revenue bar chart',
      'Market share pie chart',
      'Growth trend line graph',
      'Geographic heat map',
    ],
    prompt: 'Design report charts',
    tips: [
      'Choose appropriate chart types',
      'Label axes clearly',
      'Use color meaningfully',
      'Include data sources',
    ],
    metaTitle: 'Report Charts Generator - Create Business Visualizations | SVG.new',
    metaDescription: 'Design professional charts and graphs for business reports with AI tools.',
    keywords: ['report charts', 'business charts', 'data graphs', 'report visualization'],
  },

  // More Decorative Templates
  {
    id: 'mandala-art',
    slug: 'mandala-art',
    title: 'Mandala Designs',
    description: 'Create intricate mandala patterns for meditation and decoration',
    category: 'Decorative',
    icon: '☸️',
    previewImage: `https://via.placeholder.com/400x300/4299e1/ffffff?text=Mandala`,
    galleryImages: [],
    gradient: 'from-indigo-500 to-purple-600',
    examples: [
      'Traditional circular mandala',
      'Geometric modern mandala',
      'Floral mandala pattern',
      'Celtic knot mandala design',
    ],
    prompt: 'Create a mandala design',
    tips: [
      'Maintain perfect symmetry',
      'Build from center outward',
      'Use repeating elements',
      'Consider coloring book use',
    ],
    metaTitle: 'Mandala Design Generator - Create Sacred Geometry Art | SVG.new',
    metaDescription: 'Generate beautiful mandala designs and sacred geometry patterns with AI.',
    keywords: ['mandala design', 'sacred geometry', 'meditation art', 'decorative mandala'],
  },
  {
    id: 'wallpaper-pattern',
    slug: 'wallpaper-pattern',
    title: 'Wallpaper Patterns',
    description: 'Design repeating patterns for desktop and mobile wallpapers',
    category: 'Decorative',
    icon: '🖼️',
    previewImage: `https://via.placeholder.com/400x300/4299e1/ffffff?text=Wallpaper`,
    galleryImages: [],
    gradient: 'from-green-500 to-blue-600',
    examples: [
      'Abstract gradient wallpaper',
      'Minimalist geometric pattern',
      'Nature-inspired wallpaper',
      'Dark mode OLED wallpaper',
    ],
    prompt: 'Create wallpaper pattern',
    tips: [
      'Consider different screen resolutions',
      'Test on various devices',
      'Avoid distracting elements',
      'Optimize for battery life (OLED)',
    ],
    metaTitle: 'Wallpaper Pattern Generator - Create Desktop & Mobile Backgrounds | SVG.new',
    metaDescription: 'Design beautiful wallpaper patterns for desktop and mobile devices with AI.',
    keywords: ['wallpaper pattern', 'desktop wallpaper', 'mobile background', 'background design'],
  },

  // === Extended Templates Set 2 (42) ===
  // More templates for comprehensive coverage
  // More Web Design Templates
  {
    id: 'newsletter-header',
    slug: 'newsletter-header',
    title: 'Newsletter Headers',
    description: 'Eye-catching headers for email newsletters and campaigns',
    category: 'Web Design',
    icon: '📧',
    previewImage: '/images/templates/newsletter-header/svg-43ca9a87.svg',
    galleryImages: [
      '/images/templates/newsletter-header/svg-424eaa54.svg',
      '/images/templates/newsletter-header/svg-4815956e.svg',
      '/images/templates/newsletter-header/svg-47c899b2.svg',
      '/images/templates/newsletter-header/svg-4e34bf99.svg',
    ],
    gradient: 'from-purple-500 to-blue-600',
    examples: [
      'Tech newsletter header with circuit patterns',
      'Weekly digest header with geometric shapes',
      'Product update newsletter banner',
      'Holiday themed email header',
    ],
    prompt: 'Design a newsletter header',
    tips: [
      'Keep design email-client friendly',
      'Use web-safe fonts',
      'Optimize for mobile viewing',
      'Include clear CTAs',
    ],
    metaTitle: 'Newsletter Header Designer - Create Email Headers | SVG.new',
    metaDescription: 'Design professional newsletter headers for email campaigns with AI.',
    keywords: ['newsletter header', 'email design', 'email banner', 'newsletter graphics'],
  },
  {
    id: 'social-media-banner',
    slug: 'social-media-banner',
    title: 'Social Media Banners',
    description: 'Professional banners for Twitter, LinkedIn, and Facebook profiles',
    category: 'Web Design',
    icon: '🎯',
    previewImage: `https://via.placeholder.com/400x300/667eea/ffffff?text=Social+Banner`,
    galleryImages: [],
    gradient: 'from-pink-500 to-purple-600',
    examples: [
      'LinkedIn professional banner',
      'Twitter header with brand colors',
      'Facebook cover photo design',
      'YouTube channel banner',
    ],
    prompt: 'Create a social media banner',
    tips: [
      'Follow platform size guidelines',
      'Account for profile photo placement',
      'Keep text readable on mobile',
      'Use brand colors consistently',
    ],
    metaTitle: 'Social Media Banner Creator - Design Profile Headers | SVG.new',
    metaDescription:
      'Create stunning social media banners for Twitter, LinkedIn, and Facebook with AI.',
    keywords: ['social media banner', 'twitter header', 'linkedin banner', 'facebook cover'],
  },
  {
    id: 'loading-animation',
    slug: 'loading-animation',
    title: 'Loading Animations',
    description: 'Custom loading spinners and progress indicators for web apps',
    category: 'Web Design',
    icon: '⏳',
    previewImage: `https://via.placeholder.com/400x300/667eea/ffffff?text=Loading`,
    galleryImages: [],
    gradient: 'from-cyan-500 to-blue-600',
    examples: [
      'Circular progress spinner',
      'Skeleton screen loader',
      'Dots animation loader',
      'Bar progress indicator',
    ],
    prompt: 'Design a loading animation',
    tips: [
      'Keep animations smooth',
      'Ensure accessibility',
      'Optimize for performance',
      'Match brand aesthetics',
    ],
    metaTitle: 'Loading Animation Generator - Create Web Spinners | SVG.new',
    metaDescription:
      'Design custom loading animations and progress indicators for web applications.',
    keywords: ['loading animation', 'spinner', 'progress indicator', 'web loader'],
  },

  // More Digital Art Templates
  {
    id: 'pixel-art',
    slug: 'pixel-art',
    title: 'Pixel Art Graphics',
    description: 'Retro-style pixel art for games and digital projects',
    category: 'Digital Art',
    icon: '👾',
    previewImage: '/images/templates/pixel-art/svg-4729924f.svg',
    galleryImages: [
      '/images/templates/pixel-art/svg-4a41b7c4.svg',
      '/images/templates/pixel-art/svg-4e41b207.svg',
      '/images/templates/pixel-art/svg-4cd991a9.svg',
      '/images/templates/pixel-art/svg-4517af5f.svg',
    ],
    gradient: 'from-green-400 to-blue-500',
    examples: [
      '8-bit character sprite',
      'Retro game item icon',
      'Pixel art landscape',
      'Animated pixel avatar',
    ],
    prompt: 'Create pixel art',
    tips: [
      'Work with limited color palette',
      'Use consistent pixel size',
      'Focus on clear silhouettes',
      'Test at different scales',
    ],
    metaTitle: 'Pixel Art Generator - Create Retro Graphics | SVG.new',
    metaDescription: 'Design pixel art graphics for games, avatars, and retro-style projects.',
    keywords: ['pixel art', '8-bit art', 'retro graphics', 'sprite design'],
  },
  {
    id: 'abstract-background',
    slug: 'abstract-background',
    title: 'Abstract Backgrounds',
    description: 'Unique abstract designs for presentations and digital media',
    category: 'Digital Art',
    icon: '🎨',
    previewImage: `/images/templates/abstract-background/svg-4301ae6e.svg`,
    galleryImages: [
      '/images/templates/abstract-background/svg-448cb47f.svg',
      '/images/templates/abstract-background/svg-4772845f.svg',
      '/images/templates/abstract-background/svg-47788ecf.svg',
      '/images/templates/abstract-background/svg-4a1882c9.svg',
    ],
    gradient: 'from-purple-400 to-pink-600',
    examples: [
      'Gradient mesh background',
      'Geometric abstract pattern',
      'Fluid shapes composition',
      'Minimalist abstract design',
    ],
    prompt: 'Design abstract background',
    tips: [
      'Balance colors and shapes',
      'Consider usage context',
      'Maintain visual flow',
      'Test with overlaid content',
    ],
    metaTitle: 'Abstract Background Generator - Create Digital Art | SVG.new',
    metaDescription: 'Generate unique abstract backgrounds for presentations and digital projects.',
    keywords: ['abstract background', 'abstract art', 'digital background', 'artistic design'],
  },
  {
    id: 'meme-template',
    slug: 'meme-template',
    title: 'Meme Templates',
    description: 'Create custom meme formats and reaction images',
    category: 'Digital Art',
    icon: '😂',
    previewImage: `https://via.placeholder.com/400x300/f687b3/ffffff?text=Meme`,
    galleryImages: [],
    gradient: 'from-yellow-400 to-orange-500',
    examples: [
      'Drake meme format',
      'Expanding brain template',
      'Reaction image design',
      'Custom meme character',
    ],
    prompt: 'Create a meme template',
    tips: [
      'Keep it simple and clear',
      'Leave space for text',
      'Make expressions obvious',
      'Consider viral potential',
    ],
    metaTitle: 'Meme Template Creator - Design Custom Memes | SVG.new',
    metaDescription: 'Design custom meme templates and reaction images for social media.',
    keywords: ['meme template', 'meme creator', 'reaction image', 'viral meme'],
  },

  // More UI/UX Templates
  {
    id: 'onboarding-screen',
    slug: 'onboarding-screen',
    title: 'Onboarding Screens',
    description: 'Welcome screens and tutorials for mobile apps',
    category: 'UI/UX',
    icon: '📱',
    previewImage: `https://via.placeholder.com/400x300/48bb78/ffffff?text=Onboarding`,
    galleryImages: [],
    gradient: 'from-blue-500 to-green-500',
    examples: [
      'App feature walkthrough',
      'Welcome screen illustration',
      'Tutorial step graphics',
      'Benefits showcase screens',
    ],
    prompt: 'Design onboarding screen',
    tips: [
      'Keep text minimal',
      'Use engaging illustrations',
      'Show clear value props',
      'Include progress indicators',
    ],
    metaTitle: 'Onboarding Screen Designer - Create App Tutorials | SVG.new',
    metaDescription: 'Design engaging onboarding screens and tutorials for mobile applications.',
    keywords: ['onboarding screen', 'app tutorial', 'welcome screen', 'mobile onboarding'],
  },
  {
    id: 'empty-state',
    slug: 'empty-state',
    title: 'Empty State Illustrations',
    description: 'Friendly illustrations for empty screens and no-data states',
    category: 'UI/UX',
    icon: '📭',
    previewImage: '/images/templates/empty-state/svg-4f6ea254.svg',
    galleryImages: [
      '/images/templates/empty-state/svg-4e1caddb.svg',
      '/images/templates/empty-state/svg-41e2b0d3.svg',
      '/images/templates/empty-state/svg-40469618.svg',
      '/images/templates/empty-state/svg-4964aaa4.svg',
    ],
    gradient: 'from-gray-400 to-gray-600',
    examples: [
      'No data found illustration',
      'Empty inbox graphic',
      'No results search state',
      'First time user screen',
    ],
    prompt: 'Create empty state illustration',
    tips: [
      'Keep it friendly and helpful',
      'Include actionable guidance',
      'Match app visual style',
      'Add subtle animations',
    ],
    metaTitle: 'Empty State Illustration Generator - UI Design | SVG.new',
    metaDescription: 'Create friendly empty state illustrations for apps and websites.',
    keywords: ['empty state', 'no data illustration', 'ui illustration', 'app graphics'],
  },
  {
    id: 'notification-badge',
    slug: 'notification-badge',
    title: 'Notification Badges',
    description: 'Custom notification indicators and badges for apps',
    category: 'UI/UX',
    icon: '🔔',
    previewImage: `https://via.placeholder.com/400x300/48bb78/ffffff?text=Badge`,
    galleryImages: [],
    gradient: 'from-red-500 to-pink-600',
    examples: [
      'Unread message counter',
      'Achievement badge design',
      'Status indicator badge',
      'New feature highlight',
    ],
    prompt: 'Design notification badge',
    tips: [
      'Ensure high visibility',
      'Keep numbers readable',
      'Use attention-grabbing colors',
      'Test at small sizes',
    ],
    metaTitle: 'Notification Badge Designer - Create UI Indicators | SVG.new',
    metaDescription: 'Design custom notification badges and indicators for applications.',
    keywords: ['notification badge', 'ui badge', 'app indicator', 'counter badge'],
  },

  // More Fashion Templates
  {
    id: 'shoe-design',
    slug: 'shoe-design',
    title: 'Shoe Graphics',
    description: 'Custom designs for sneakers and footwear',
    category: 'Fashion',
    icon: '👟',
    previewImage: `https://via.placeholder.com/400x300/f56565/ffffff?text=Shoe+Design`,
    galleryImages: [],
    gradient: 'from-blue-500 to-purple-600',
    examples: [
      'Sneaker side panel art',
      'Custom shoe pattern',
      'Athletic shoe graphics',
      'Fashion shoe prints',
    ],
    prompt: 'Design shoe graphics',
    tips: [
      'Consider shoe contours',
      'Design for different angles',
      'Use durable design elements',
      'Test on mockups',
    ],
    metaTitle: 'Shoe Design Generator - Create Footwear Graphics | SVG.new',
    metaDescription: 'Design custom graphics for sneakers and footwear with AI assistance.',
    keywords: ['shoe design', 'sneaker graphics', 'footwear design', 'custom shoes'],
  },
  {
    id: 'bag-design',
    slug: 'bag-design',
    title: 'Bag & Tote Designs',
    description: 'Graphics for bags, totes, and accessories',
    category: 'Fashion',
    icon: '👜',
    previewImage: `https://via.placeholder.com/400x300/f56565/ffffff?text=Bag+Design`,
    galleryImages: [],
    gradient: 'from-pink-500 to-red-600',
    examples: [
      'Canvas tote bag design',
      'Backpack pattern graphics',
      'Handbag monogram design',
      'Eco bag illustration',
    ],
    prompt: 'Create bag design',
    tips: [
      'Consider bag dimensions',
      'Design for durability',
      'Account for seams and handles',
      'Create versatile designs',
    ],
    metaTitle: 'Bag Design Generator - Create Tote Graphics | SVG.new',
    metaDescription: 'Design custom graphics for bags, totes, and accessories.',
    keywords: ['bag design', 'tote graphics', 'accessory design', 'handbag pattern'],
  },
  {
    id: 'jewelry-design',
    slug: 'jewelry-design',
    title: 'Jewelry Patterns',
    description: 'Designs for custom jewelry and accessories',
    category: 'Fashion',
    icon: '💎',
    previewImage: `https://via.placeholder.com/400x300/f56565/ffffff?text=Jewelry`,
    galleryImages: [],
    gradient: 'from-yellow-400 to-yellow-600',
    examples: [
      'Pendant design patterns',
      'Ring engraving designs',
      'Bracelet charm graphics',
      'Earring shape designs',
    ],
    prompt: 'Design jewelry pattern',
    tips: [
      'Keep designs delicate',
      'Consider material constraints',
      'Focus on symmetry',
      'Design for different scales',
    ],
    metaTitle: 'Jewelry Design Generator - Create Accessory Patterns | SVG.new',
    metaDescription: 'Design patterns for custom jewelry and accessories with AI.',
    keywords: ['jewelry design', 'accessory pattern', 'pendant design', 'custom jewelry'],
  },

  // More Branding Templates
  {
    id: 'email-signature',
    slug: 'email-signature',
    title: 'Email Signatures',
    description: 'Professional email signature designs with social links',
    category: 'Branding',
    icon: '✉️',
    previewImage: `https://via.placeholder.com/400x300/805ad5/ffffff?text=Email+Sig`,
    galleryImages: [],
    gradient: 'from-gray-600 to-gray-800',
    examples: [
      'Corporate email signature',
      'Creative agency signature',
      'Personal brand signature',
      'Team email template',
    ],
    prompt: 'Design email signature',
    tips: [
      'Keep it concise',
      'Include key contact info',
      'Use web-safe formatting',
      'Add social media links',
    ],
    metaTitle: 'Email Signature Designer - Create Professional Signatures | SVG.new',
    metaDescription: 'Design professional email signatures with social links and branding.',
    keywords: ['email signature', 'email footer', 'signature design', 'professional signature'],
  },
  {
    id: 'invoice-template',
    slug: 'invoice-template',
    title: 'Invoice Templates',
    description: 'Professional invoice designs for businesses',
    category: 'Branding',
    icon: '📃',
    previewImage: `https://via.placeholder.com/400x300/805ad5/ffffff?text=Invoice`,
    galleryImages: [],
    gradient: 'from-blue-600 to-indigo-700',
    examples: [
      'Modern invoice layout',
      'Classic invoice design',
      'Creative invoice template',
      'Minimalist billing form',
    ],
    prompt: 'Create invoice template',
    tips: [
      'Include all legal requirements',
      'Organize information clearly',
      'Leave space for details',
      'Match brand guidelines',
    ],
    metaTitle: 'Invoice Template Designer - Create Business Forms | SVG.new',
    metaDescription: 'Design professional invoice templates for business billing.',
    keywords: ['invoice template', 'billing form', 'invoice design', 'business invoice'],
  },
  {
    id: 'brand-guidelines',
    slug: 'brand-guidelines',
    title: 'Brand Guidelines',
    description: 'Visual style guide templates for brand consistency',
    category: 'Branding',
    icon: '📋',
    previewImage: `https://via.placeholder.com/400x300/805ad5/ffffff?text=Guidelines`,
    galleryImages: [],
    gradient: 'from-purple-600 to-pink-600',
    examples: [
      'Color palette guide',
      'Typography standards',
      'Logo usage guidelines',
      'Visual identity manual',
    ],
    prompt: 'Design brand guidelines',
    tips: [
      'Be comprehensive yet clear',
      "Show do's and don'ts",
      'Include real examples',
      'Make it easy to follow',
    ],
    metaTitle: 'Brand Guidelines Generator - Create Style Guides | SVG.new',
    metaDescription: 'Create comprehensive brand guidelines and style guides with AI.',
    keywords: ['brand guidelines', 'style guide', 'brand manual', 'visual identity'],
  },

  // More Gaming Templates
  {
    id: 'achievement-icon',
    slug: 'achievement-icon',
    title: 'Achievement Icons',
    description: 'Trophy and achievement icons for games',
    category: 'Gaming',
    icon: '🏆',
    previewImage: '/images/templates/achievement-icon/Gold Trophy Icon-491b8be7.svg',
    galleryImages: [
      '/images/templates/achievement-icon/Skill Badge Achievement Icon-4d8e8663.svg',
      '/images/templates/achievement-icon/Achievement Medal Icon-485cb082.svg',
      '/images/templates/achievement-icon/Achievement Trophy Icon-43c1a012.svg',
      '/images/templates/achievement-icon/Skill Badge Achievement Icon-42a089ed.svg',
    ],
    gradient: 'from-yellow-500 to-amber-600',
    examples: [
      'Gold trophy icon',
      'Skill badge achievement',
      'Level completion medal',
      'Special event trophy',
    ],
    prompt: 'Design achievement icon',
    tips: [
      'Make it feel rewarding',
      'Use metallic effects',
      'Create clear hierarchy',
      'Design for small display',
    ],
    metaTitle: 'Achievement Icon Generator - Create Game Trophies | SVG.new',
    metaDescription: 'Design achievement icons and trophies for games and apps.',
    keywords: ['achievement icon', 'game trophy', 'badge design', 'reward icon'],
  },
  {
    id: 'game-logo',
    slug: 'game-logo',
    title: 'Game Logos',
    description: 'Logo designs for video games and gaming brands',
    category: 'Gaming',
    icon: '🎮',
    previewImage: `https://via.placeholder.com/400x300/ed8936/ffffff?text=Game+Logo`,
    galleryImages: [],
    gradient: 'from-red-500 to-orange-600',
    examples: ['Action game logo', 'RPG title design', 'Mobile game logo', 'Esports team brand'],
    prompt: 'Create game logo',
    tips: [
      'Make it memorable',
      'Consider game genre',
      'Ensure readability',
      'Test at different sizes',
    ],
    metaTitle: 'Game Logo Generator - Create Gaming Brands | SVG.new',
    metaDescription: 'Design professional logos for video games and gaming brands.',
    keywords: ['game logo', 'gaming brand', 'video game logo', 'esports logo'],
  },
  {
    id: 'game-item',
    slug: 'game-item',
    title: 'Game Item Icons',
    description: 'Inventory items and power-ups for games',
    category: 'Gaming',
    icon: '⚔️',
    previewImage: `https://via.placeholder.com/400x300/ed8936/ffffff?text=Game+Item`,
    galleryImages: [],
    gradient: 'from-purple-500 to-indigo-600',
    examples: [
      'Magic potion bottle',
      'Legendary sword icon',
      'Health pack design',
      'Treasure chest graphic',
    ],
    prompt: 'Design game item icon',
    tips: [
      'Make items recognizable',
      'Use consistent style',
      'Show rarity clearly',
      'Design for inventory grids',
    ],
    metaTitle: 'Game Item Icon Generator - Create Inventory Graphics | SVG.new',
    metaDescription: 'Design game item icons and inventory graphics for video games.',
    keywords: ['game item', 'inventory icon', 'game asset', 'item design'],
  },

  // More Illustration Templates
  {
    id: 'comic-panel',
    slug: 'comic-panel',
    title: 'Comic Panels',
    description: 'Comic book style panels and speech bubbles',
    category: 'Illustration',
    icon: '💬',
    previewImage: `https://via.placeholder.com/400x300/9f7aea/ffffff?text=Comic`,
    galleryImages: [],
    gradient: 'from-red-500 to-yellow-500',
    examples: [
      'Action scene panel',
      'Dialogue bubble layout',
      'Manga style panel',
      'Webcomic strip format',
    ],
    prompt: 'Create comic panel',
    tips: [
      "Guide reader's eye flow",
      'Use dynamic angles',
      'Balance text and art',
      'Create clear gutters',
    ],
    metaTitle: 'Comic Panel Generator - Create Comic Layouts | SVG.new',
    metaDescription: 'Design comic panels and speech bubbles for graphic novels.',
    keywords: ['comic panel', 'speech bubble', 'comic layout', 'graphic novel'],
  },
  {
    id: 'album-cover',
    slug: 'album-cover',
    title: 'Album Covers',
    description: 'Music album artwork for digital and physical releases',
    category: 'Illustration',
    icon: '🎵',
    previewImage: '/images/templates/album-cover/svg-45d6b2b2.svg',
    galleryImages: [
      '/images/templates/album-cover/svg-49ef81fd.svg',
      '/images/templates/album-cover/svg-4a6d98fb.svg',
      '/images/templates/album-cover/svg-43b68920.svg',
      '/images/templates/album-cover/svg-4f77b7e6.svg',
    ],
    gradient: 'from-pink-500 to-purple-700',
    examples: [
      'Electronic music cover',
      'Rock album artwork',
      'Minimalist album design',
      'Hip-hop mixtape cover',
    ],
    prompt: 'Design album cover',
    tips: [
      'Reflect music genre',
      'Consider square format',
      'Make it iconic',
      'Test at thumbnail size',
    ],
    metaTitle: 'Album Cover Generator - Create Music Artwork | SVG.new',
    metaDescription: 'Design stunning album covers for music releases with AI.',
    keywords: ['album cover', 'music artwork', 'album art', 'cover design'],
  },
  {
    id: 'children-illustration',
    slug: 'children-illustration',
    title: "Children's Illustrations",
    description: "Playful illustrations for children's books and content",
    category: 'Illustration',
    icon: '🧸',
    previewImage: `https://via.placeholder.com/400x300/9f7aea/ffffff?text=Kids`,
    galleryImages: [],
    gradient: 'from-yellow-400 to-pink-500',
    examples: [
      'Storybook character',
      'Educational illustration',
      'Nursery wall art',
      'Activity book graphics',
    ],
    prompt: "Create children's illustration",
    tips: [
      'Use bright, happy colors',
      'Keep shapes simple',
      'Make characters friendly',
      'Add playful elements',
    ],
    metaTitle: "Children's Illustration Generator - Create Kids Art | SVG.new",
    metaDescription: "Design playful illustrations for children's books and content.",
    keywords: ['children illustration', 'kids art', 'storybook art', 'nursery design'],
  },

  // More Data Viz Templates
  {
    id: 'flowchart',
    slug: 'flowchart',
    title: 'Flowcharts',
    description: 'Process flow diagrams and decision trees',
    category: 'Data Viz',
    icon: '🔀',
    previewImage: `https://via.placeholder.com/400x300/38b2ac/ffffff?text=Flowchart`,
    galleryImages: [],
    gradient: 'from-blue-500 to-cyan-600',
    examples: [
      'Business process flow',
      'User journey diagram',
      'Decision tree chart',
      'System architecture flow',
    ],
    prompt: 'Create flowchart',
    tips: ['Use standard symbols', 'Keep flow logical', 'Label clearly', 'Minimize crossing lines'],
    metaTitle: 'Flowchart Generator - Create Process Diagrams | SVG.new',
    metaDescription: 'Design professional flowcharts and process diagrams with AI.',
    keywords: ['flowchart', 'process diagram', 'decision tree', 'flow diagram'],
  },
  {
    id: 'mind-map',
    slug: 'mind-map',
    title: 'Mind Maps',
    description: 'Visual thinking tools for brainstorming and planning',
    category: 'Data Viz',
    icon: '🧠',
    previewImage: `https://via.placeholder.com/400x300/38b2ac/ffffff?text=Mind+Map`,
    galleryImages: [],
    gradient: 'from-purple-500 to-pink-600',
    examples: [
      'Project planning map',
      'Concept brainstorm',
      'Study notes diagram',
      'Creative idea web',
    ],
    prompt: 'Design mind map',
    tips: [
      'Start from central concept',
      'Use colors for categories',
      'Keep branches organized',
      'Add visual elements',
    ],
    metaTitle: 'Mind Map Generator - Create Visual Thinking Tools | SVG.new',
    metaDescription: 'Design mind maps for brainstorming and visual planning.',
    keywords: ['mind map', 'brainstorm diagram', 'concept map', 'idea visualization'],
  },
  {
    id: 'comparison-chart',
    slug: 'comparison-chart',
    title: 'Comparison Charts',
    description: 'Side-by-side comparison tables and graphics',
    category: 'Data Viz',
    icon: '⚖️',
    previewImage: `https://via.placeholder.com/400x300/38b2ac/ffffff?text=Compare`,
    galleryImages: [],
    gradient: 'from-green-500 to-teal-600',
    examples: [
      'Product comparison table',
      'Pricing tier chart',
      'Feature matrix design',
      'Pros and cons layout',
    ],
    prompt: 'Create comparison chart',
    tips: [
      'Highlight differences',
      'Use consistent formatting',
      'Make data scannable',
      'Include visual cues',
    ],
    metaTitle: 'Comparison Chart Generator - Create Data Tables | SVG.new',
    metaDescription: 'Design comparison charts and tables for products and services.',
    keywords: ['comparison chart', 'comparison table', 'feature matrix', 'data comparison'],
  },

  // More Decorative Templates
  {
    id: 'border-design',
    slug: 'border-design',
    title: 'Border Designs',
    description: 'Decorative borders and frames for documents',
    category: 'Decorative',
    icon: '🖼️',
    previewImage: `https://via.placeholder.com/400x300/4299e1/ffffff?text=Border`,
    galleryImages: [],
    gradient: 'from-gold-500 to-amber-600',
    examples: [
      'Certificate border',
      'Vintage frame design',
      'Modern geometric border',
      'Floral decorative frame',
    ],
    prompt: 'Design decorative border',
    tips: [
      'Match document style',
      'Keep corners balanced',
      'Consider print margins',
      'Test with content',
    ],
    metaTitle: 'Border Design Generator - Create Decorative Frames | SVG.new',
    metaDescription: 'Design decorative borders and frames for documents and certificates.',
    keywords: ['border design', 'decorative frame', 'certificate border', 'document frame'],
  },
  {
    id: 'divider-element',
    slug: 'divider-element',
    title: 'Section Dividers',
    description: 'Decorative dividers for separating content sections',
    category: 'Decorative',
    icon: '➖',
    previewImage: `https://via.placeholder.com/400x300/4299e1/ffffff?text=Divider`,
    galleryImages: [],
    gradient: 'from-gray-500 to-gray-700',
    examples: [
      'Ornamental page divider',
      'Minimalist line separator',
      'Decorative flourish',
      'Geometric section break',
    ],
    prompt: 'Create section divider',
    tips: [
      'Keep it subtle',
      'Match overall design',
      'Ensure proper spacing',
      'Consider different widths',
    ],
    metaTitle: 'Section Divider Generator - Create Content Separators | SVG.new',
    metaDescription: 'Design decorative dividers for separating content sections.',
    keywords: ['section divider', 'content separator', 'page divider', 'decorative line'],
  },
  {
    id: 'monogram-design',
    slug: 'monogram-design',
    title: 'Monogram Designs',
    description: 'Personal monograms and initial designs',
    category: 'Decorative',
    icon: '🔤',
    previewImage: `https://via.placeholder.com/400x300/4299e1/ffffff?text=Monogram`,
    galleryImages: [],
    gradient: 'from-purple-600 to-indigo-700',
    examples: [
      'Wedding monogram',
      'Personal brand initials',
      'Family crest design',
      'Corporate monogram',
    ],
    prompt: 'Design monogram',
    tips: [
      'Balance letter forms',
      'Create unique combinations',
      'Consider versatility',
      'Test at various sizes',
    ],
    metaTitle: 'Monogram Design Generator - Create Initial Logos | SVG.new',
    metaDescription: 'Design elegant monograms and initial combinations.',
    keywords: ['monogram design', 'initial logo', 'personal monogram', 'letter design'],
  },

  // === Extended Templates Set 3 (40) ===
  // Templates inspired by real-world demand
  // Business & Finance Category
  {
    id: 'business-presentation',
    slug: 'business-presentation',
    title: 'Business Presentations',
    description: 'Professional slides and graphics for corporate presentations',
    category: 'Branding',
    icon: '📊',
    previewImage: `https://via.placeholder.com/400x300/2563eb/ffffff?text=Presentation`,
    galleryImages: [],
    gradient: 'from-blue-600 to-blue-800',
    examples: [
      'Quarterly report slides',
      'Sales pitch deck',
      'Company overview presentation',
      'Financial data visualization',
    ],
    prompt: 'Create business presentation graphic',
    tips: [
      'Keep data clear and concise',
      'Use corporate colors',
      'Maintain visual hierarchy',
      'Ensure readability',
    ],
    metaTitle: 'Business Presentation Designer - Create Corporate Slides | SVG.new',
    metaDescription: 'Design professional business presentations and corporate slides with AI.',
    keywords: ['business presentation', 'corporate slides', 'pitch deck', 'presentation design'],
  },
  {
    id: 'financial-chart',
    slug: 'financial-chart',
    title: 'Financial Charts',
    description: 'Stock market and financial data visualizations',
    category: 'Data Viz',
    icon: '📈',
    previewImage: `https://via.placeholder.com/400x300/10b981/ffffff?text=Finance`,
    galleryImages: [],
    gradient: 'from-green-500 to-emerald-700',
    examples: [
      'Stock price chart',
      'Portfolio performance graph',
      'Revenue growth visualization',
      'Market trend analysis',
    ],
    prompt: 'Design financial chart',
    tips: [
      'Use standard financial colors',
      'Show clear trends',
      'Include proper labels',
      'Add context data',
    ],
    metaTitle: 'Financial Chart Generator - Create Stock Market Graphics | SVG.new',
    metaDescription: 'Design professional financial charts and market visualizations.',
    keywords: ['financial chart', 'stock market', 'trading chart', 'finance visualization'],
  },

  // Healthcare & Medical
  {
    id: 'medical-illustration',
    slug: 'medical-illustration',
    title: 'Medical Illustrations',
    description: 'Healthcare and medical diagrams for education',
    category: 'Illustration',
    icon: '⚕️',
    previewImage: `https://via.placeholder.com/400x300/ef4444/ffffff?text=Medical`,
    galleryImages: [],
    gradient: 'from-red-500 to-pink-600',
    examples: [
      'Anatomy diagram',
      'Health infographic',
      'Medical procedure illustration',
      'Healthcare icon set',
    ],
    prompt: 'Create medical illustration',
    tips: ['Ensure accuracy', 'Use clear labeling', 'Keep it educational', 'Consider sensitivity'],
    metaTitle: 'Medical Illustration Generator - Healthcare Graphics | SVG.new',
    metaDescription: 'Create medical illustrations and healthcare diagrams with AI.',
    keywords: [
      'medical illustration',
      'healthcare graphics',
      'anatomy diagram',
      'health infographic',
    ],
  },
  {
    id: 'wellness-icon',
    slug: 'wellness-icon',
    title: 'Wellness Icons',
    description: 'Health and wellness icons for apps and websites',
    category: 'UI/UX',
    icon: '🧘',
    previewImage: `https://via.placeholder.com/400x300/8b5cf6/ffffff?text=Wellness`,
    galleryImages: [],
    gradient: 'from-purple-500 to-pink-500',
    examples: [
      'Meditation app icons',
      'Fitness tracking symbols',
      'Nutrition icons',
      'Mental health graphics',
    ],
    prompt: 'Design wellness icon',
    tips: [
      'Use calming colors',
      'Keep designs peaceful',
      'Ensure clarity',
      'Consider accessibility',
    ],
    metaTitle: 'Wellness Icon Generator - Health App Graphics | SVG.new',
    metaDescription: 'Design wellness and health icons for applications and websites.',
    keywords: ['wellness icon', 'health icon', 'fitness icon', 'meditation graphic'],
  },

  // Education & Learning
  {
    id: 'educational-poster',
    slug: 'educational-poster',
    title: 'Educational Posters',
    description: 'Learning materials and classroom posters',
    category: 'Illustration',
    icon: '📚',
    previewImage: `https://via.placeholder.com/400x300/f59e0b/ffffff?text=Education`,
    galleryImages: [],
    gradient: 'from-yellow-500 to-orange-600',
    examples: [
      'Science classroom poster',
      'Math concept diagram',
      'Language learning chart',
      'History timeline graphic',
    ],
    prompt: 'Create educational poster',
    tips: ['Make it engaging', 'Use clear typography', 'Add visual aids', 'Consider age group'],
    metaTitle: 'Educational Poster Generator - Classroom Materials | SVG.new',
    metaDescription: 'Design educational posters and learning materials for classrooms.',
    keywords: ['educational poster', 'classroom poster', 'learning materials', 'teaching aids'],
  },
  {
    id: 'certificate-design',
    slug: 'certificate-design',
    title: 'Certificates',
    description: 'Achievement certificates and diplomas',
    category: 'Branding',
    icon: '🎓',
    previewImage: `https://via.placeholder.com/400x300/6366f1/ffffff?text=Certificate`,
    galleryImages: [],
    gradient: 'from-indigo-500 to-blue-700',
    examples: [
      'Course completion certificate',
      'Award certificate',
      'Professional certification',
      'Academic diploma',
    ],
    prompt: 'Design certificate',
    tips: [
      'Use formal design',
      'Include security elements',
      'Leave space for details',
      'Add official seals',
    ],
    metaTitle: 'Certificate Design Generator - Create Diplomas | SVG.new',
    metaDescription: 'Design professional certificates and diplomas with AI.',
    keywords: [
      'certificate design',
      'diploma template',
      'award certificate',
      'achievement certificate',
    ],
  },

  // Real Estate & Architecture
  {
    id: 'real-estate-flyer',
    slug: 'real-estate-flyer',
    title: 'Real Estate Flyers',
    description: 'Property listings and real estate marketing materials',
    category: 'Web Design',
    icon: '🏠',
    previewImage: `https://via.placeholder.com/400x300/059669/ffffff?text=Real+Estate`,
    galleryImages: [],
    gradient: 'from-teal-600 to-green-700',
    examples: [
      'Property listing flyer',
      'Open house announcement',
      'Real estate agent branding',
      'Property showcase brochure',
    ],
    prompt: 'Create real estate flyer',
    tips: [
      'Highlight key features',
      'Use professional photos',
      'Include contact info',
      'Show location benefits',
    ],
    metaTitle: 'Real Estate Flyer Generator - Property Marketing | SVG.new',
    metaDescription: 'Design real estate flyers and property marketing materials.',
    keywords: ['real estate flyer', 'property listing', 'real estate marketing', 'house flyer'],
  },
  {
    id: 'floor-plan',
    slug: 'floor-plan',
    title: 'Floor Plans',
    description: 'Architectural floor plans and layouts',
    category: 'Data Viz',
    icon: '📐',
    previewImage: `https://via.placeholder.com/400x300/64748b/ffffff?text=Floor+Plan`,
    galleryImages: [],
    gradient: 'from-gray-600 to-slate-800',
    examples: [
      'Apartment layout',
      'House floor plan',
      'Office space design',
      'Retail store layout',
    ],
    prompt: 'Design floor plan',
    tips: ['Use standard symbols', 'Show accurate scale', 'Label all rooms', 'Include dimensions'],
    metaTitle: 'Floor Plan Generator - Architectural Layouts | SVG.new',
    metaDescription: 'Create floor plans and architectural layouts with AI.',
    keywords: ['floor plan', 'architectural layout', 'house plan', 'building design'],
  },

  // Food & Restaurant
  {
    id: 'menu-design',
    slug: 'menu-design',
    title: 'Restaurant Menus',
    description: 'Menu designs for restaurants and cafes',
    category: 'Branding',
    icon: '🍽️',
    previewImage: `https://via.placeholder.com/400x300/dc2626/ffffff?text=Menu`,
    galleryImages: [],
    gradient: 'from-red-600 to-orange-700',
    examples: [
      'Restaurant dinner menu',
      'Cafe beverage menu',
      'Bar cocktail list',
      'Food truck menu board',
    ],
    prompt: 'Design restaurant menu',
    tips: [
      'Organize by categories',
      'Use appetizing colors',
      'Make prices clear',
      'Include descriptions',
    ],
    metaTitle: 'Restaurant Menu Designer - Food Service Graphics | SVG.new',
    metaDescription: 'Design professional restaurant menus and food service materials.',
    keywords: ['restaurant menu', 'menu design', 'food menu', 'cafe menu'],
  },
  {
    id: 'recipe-card',
    slug: 'recipe-card',
    title: 'Recipe Cards',
    description: 'Visual recipe cards and cooking instructions',
    category: 'Illustration',
    icon: '👨‍🍳',
    previewImage: `https://via.placeholder.com/400x300/ea580c/ffffff?text=Recipe`,
    galleryImages: [],
    gradient: 'from-orange-500 to-red-600',
    examples: [
      'Illustrated recipe card',
      'Step-by-step cooking guide',
      'Ingredient infographic',
      'Kitchen tips card',
    ],
    prompt: 'Create recipe card',
    tips: ['Use clear steps', 'Include timing', 'Show ingredients visually', 'Add serving info'],
    metaTitle: 'Recipe Card Generator - Cooking Instructions | SVG.new',
    metaDescription: 'Design visual recipe cards and cooking instructions.',
    keywords: ['recipe card', 'cooking instructions', 'food recipe', 'recipe design'],
  },

  // Sports & Fitness
  {
    id: 'sports-logo',
    slug: 'sports-logo',
    title: 'Sports Team Logos',
    description: 'Logos for sports teams and athletic organizations',
    category: 'Branding',
    icon: '⚽',
    previewImage: `https://via.placeholder.com/400x300/0ea5e9/ffffff?text=Sports`,
    galleryImages: [],
    gradient: 'from-cyan-600 to-blue-700',
    examples: [
      'Soccer team logo',
      'Basketball team emblem',
      'Baseball club badge',
      'Esports team brand',
    ],
    prompt: 'Design sports team logo',
    tips: [
      'Make it bold and dynamic',
      'Use team colors',
      'Create strong mascot',
      'Ensure versatility',
    ],
    metaTitle: 'Sports Team Logo Generator - Athletic Branding | SVG.new',
    metaDescription: 'Design professional sports team logos and athletic branding.',
    keywords: ['sports logo', 'team logo', 'athletic logo', 'sports branding'],
  },
  {
    id: 'workout-plan',
    slug: 'workout-plan',
    title: 'Workout Plans',
    description: 'Exercise routines and fitness planning graphics',
    category: 'Illustration',
    icon: '💪',
    previewImage: `https://via.placeholder.com/400x300/84cc16/ffffff?text=Workout`,
    galleryImages: [],
    gradient: 'from-lime-500 to-green-700',
    examples: [
      'Daily workout routine',
      'Exercise form guide',
      'Fitness challenge chart',
      'Training schedule',
    ],
    prompt: 'Create workout plan',
    tips: ['Show clear movements', 'Include reps and sets', 'Add rest periods', 'Show progression'],
    metaTitle: 'Workout Plan Generator - Fitness Graphics | SVG.new',
    metaDescription: 'Design workout plans and fitness routine graphics.',
    keywords: ['workout plan', 'exercise routine', 'fitness plan', 'training schedule'],
  },

  // Travel & Tourism
  {
    id: 'travel-poster',
    slug: 'travel-poster',
    title: 'Travel Posters',
    description: 'Vintage and modern travel destination posters',
    category: 'Illustration',
    icon: '✈️',
    previewImage: `https://via.placeholder.com/400x300/7c3aed/ffffff?text=Travel`,
    galleryImages: [],
    gradient: 'from-purple-600 to-indigo-700',
    examples: [
      'Vintage travel poster',
      'City tourism guide',
      'Beach destination ad',
      'Adventure travel graphic',
    ],
    prompt: 'Design travel poster',
    tips: [
      'Capture destination essence',
      'Use iconic landmarks',
      'Create wanderlust',
      'Include key attractions',
    ],
    metaTitle: 'Travel Poster Generator - Tourism Graphics | SVG.new',
    metaDescription: 'Design stunning travel posters and tourism marketing materials.',
    keywords: ['travel poster', 'tourism poster', 'destination marketing', 'travel graphics'],
  },
  {
    id: 'map-illustration',
    slug: 'map-illustration',
    title: 'Map Illustrations',
    description: 'Custom illustrated maps and location graphics',
    category: 'Illustration',
    icon: '🗺️',
    previewImage: `https://via.placeholder.com/400x300/0891b2/ffffff?text=Map`,
    galleryImages: [],
    gradient: 'from-sky-600 to-blue-800',
    examples: [
      'Tourist attraction map',
      'Wedding venue map',
      'Campus illustrated map',
      'Festival grounds layout',
    ],
    prompt: 'Create map illustration',
    tips: ['Include landmarks', 'Use clear labels', 'Add directional cues', 'Make it readable'],
    metaTitle: 'Map Illustration Generator - Custom Maps | SVG.new',
    metaDescription: 'Design custom illustrated maps and location graphics.',
    keywords: ['map illustration', 'custom map', 'illustrated map', 'location graphic'],
  },

  // Technology & AI
  {
    id: 'ai-concept',
    slug: 'ai-concept',
    title: 'AI Concept Art',
    description: 'Artificial intelligence and machine learning visualizations',
    category: 'Digital Art',
    icon: '🤖',
    previewImage: `https://via.placeholder.com/400x300/3b82f6/ffffff?text=AI`,
    galleryImages: [],
    gradient: 'from-blue-500 to-purple-700',
    examples: [
      'Neural network visualization',
      'AI brain concept',
      'Machine learning diagram',
      'Robotics illustration',
    ],
    prompt: 'Design AI concept art',
    tips: ['Use futuristic elements', 'Show connectivity', 'Include data flows', 'Keep it modern'],
    metaTitle: 'AI Concept Art Generator - Technology Graphics | SVG.new',
    metaDescription: 'Create AI and machine learning concept visualizations.',
    keywords: ['ai concept', 'artificial intelligence', 'machine learning', 'tech illustration'],
  },
  {
    id: 'cybersecurity-icon',
    slug: 'cybersecurity-icon',
    title: 'Cybersecurity Icons',
    description: 'Security and privacy icons for tech applications',
    category: 'UI/UX',
    icon: '🔐',
    previewImage: `https://via.placeholder.com/400x300/991b1b/ffffff?text=Security`,
    galleryImages: [],
    gradient: 'from-red-700 to-gray-900',
    examples: [
      'Lock and shield icons',
      'Data protection symbols',
      'Privacy indicators',
      'Security warning graphics',
    ],
    prompt: 'Create cybersecurity icon',
    tips: ['Use security symbols', 'Convey protection', 'Keep designs clear', 'Use warning colors'],
    metaTitle: 'Cybersecurity Icon Generator - Security Graphics | SVG.new',
    metaDescription: 'Design cybersecurity icons and privacy graphics for applications.',
    keywords: ['cybersecurity icon', 'security icon', 'privacy graphic', 'protection symbol'],
  },

  // Seasonal & Holiday
  {
    id: 'holiday-card',
    slug: 'holiday-card',
    title: 'Holiday Cards',
    description: 'Greeting cards for holidays and special occasions',
    category: 'Decorative',
    icon: '🎄',
    previewImage: `https://via.placeholder.com/400x300/dc2626/ffffff?text=Holiday`,
    galleryImages: [],
    gradient: 'from-red-600 to-green-700',
    examples: [
      'Christmas greeting card',
      'New Year celebration',
      "Valentine's Day card",
      'Thanksgiving design',
    ],
    prompt: 'Design holiday card',
    tips: [
      'Use seasonal colors',
      'Include festive elements',
      'Leave space for message',
      'Create warm feeling',
    ],
    metaTitle: 'Holiday Card Generator - Greeting Card Design | SVG.new',
    metaDescription: 'Design festive holiday cards and greeting cards for special occasions.',
    keywords: ['holiday card', 'greeting card', 'christmas card', 'seasonal design'],
  },
  {
    id: 'event-invitation',
    slug: 'event-invitation',
    title: 'Event Invitations',
    description: 'Invitations for parties, weddings, and special events',
    category: 'Decorative',
    icon: '💌',
    previewImage: `https://via.placeholder.com/400x300/ec4899/ffffff?text=Invitation`,
    galleryImages: [],
    gradient: 'from-pink-500 to-rose-700',
    examples: [
      'Wedding invitation',
      'Birthday party invite',
      'Corporate event invitation',
      'Baby shower card',
    ],
    prompt: 'Create event invitation',
    tips: [
      'Include all event details',
      'Match event theme',
      'Use elegant typography',
      'Add RSVP information',
    ],
    metaTitle: 'Event Invitation Generator - Party Invites | SVG.new',
    metaDescription: 'Design beautiful event invitations for weddings, parties, and celebrations.',
    keywords: ['event invitation', 'party invite', 'wedding invitation', 'invitation design'],
  },

  // Nature & Environment
  {
    id: 'nature-scene',
    slug: 'nature-scene',
    title: 'Nature Scenes',
    description: 'Landscape and nature illustrations',
    category: 'Illustration',
    icon: '🌲',
    previewImage: `https://via.placeholder.com/400x300/22c55e/ffffff?text=Nature`,
    galleryImages: [],
    gradient: 'from-green-500 to-emerald-700',
    examples: ['Mountain landscape', 'Forest scene', 'Ocean sunset', 'Desert panorama'],
    prompt: 'Create nature scene',
    tips: [
      'Use natural colors',
      'Create depth layers',
      'Add atmospheric effects',
      'Include wildlife elements',
    ],
    metaTitle: 'Nature Scene Generator - Landscape Illustrations | SVG.new',
    metaDescription: 'Design beautiful nature scenes and landscape illustrations.',
    keywords: ['nature scene', 'landscape illustration', 'nature art', 'scenic design'],
  },
  {
    id: 'eco-badge',
    slug: 'eco-badge',
    title: 'Eco Badges',
    description: 'Environmental and sustainability badges',
    category: 'Branding',
    icon: '♻️',
    previewImage: `https://via.placeholder.com/400x300/16a34a/ffffff?text=Eco`,
    galleryImages: [],
    gradient: 'from-green-600 to-lime-700',
    examples: [
      'Recycling badge',
      'Organic certification',
      'Carbon neutral label',
      'Eco-friendly stamp',
    ],
    prompt: 'Design eco badge',
    tips: ['Use green colors', 'Include eco symbols', 'Keep message clear', 'Show sustainability'],
    metaTitle: 'Eco Badge Generator - Environmental Graphics | SVG.new',
    metaDescription: 'Design eco-friendly badges and sustainability graphics.',
    keywords: ['eco badge', 'environmental badge', 'sustainability graphic', 'green label'],
  },

  // Music & Entertainment
  {
    id: 'concert-poster',
    slug: 'concert-poster',
    title: 'Concert Posters',
    description: 'Music event and concert promotional posters',
    category: 'Illustration',
    icon: '🎸',
    previewImage: `https://via.placeholder.com/400x300/7c3aed/ffffff?text=Concert`,
    galleryImages: [],
    gradient: 'from-purple-600 to-pink-700',
    examples: [
      'Rock concert poster',
      'Music festival lineup',
      'DJ event flyer',
      'Classical concert program',
    ],
    prompt: 'Design concert poster',
    tips: [
      'Make band name prominent',
      'Include venue and date',
      'Match music genre style',
      'Create visual impact',
    ],
    metaTitle: 'Concert Poster Generator - Music Event Graphics | SVG.new',
    metaDescription: 'Design eye-catching concert posters and music event promotions.',
    keywords: ['concert poster', 'music poster', 'event poster', 'band poster'],
  },
  {
    id: 'podcast-cover',
    slug: 'podcast-cover',
    title: 'Podcast Covers',
    description: 'Cover art for podcasts and audio shows',
    category: 'Digital Art',
    icon: '🎙️',
    previewImage: `https://via.placeholder.com/400x300/6366f1/ffffff?text=Podcast`,
    galleryImages: [],
    gradient: 'from-indigo-500 to-purple-700',
    examples: [
      'True crime podcast cover',
      'Comedy show artwork',
      'Business podcast design',
      'Interview series cover',
    ],
    prompt: 'Create podcast cover',
    tips: [
      'Make title readable small',
      'Use bold colors',
      'Include host imagery',
      'Show podcast theme',
    ],
    metaTitle: 'Podcast Cover Generator - Audio Show Artwork | SVG.new',
    metaDescription: 'Design professional podcast covers and audio show artwork.',
    keywords: ['podcast cover', 'podcast art', 'audio show', 'podcast design'],
  },

  // Photography & Media
  {
    id: 'photo-overlay',
    slug: 'photo-overlay',
    title: 'Photo Overlays',
    description: 'Decorative overlays and frames for photography',
    category: 'Decorative',
    icon: '📸',
    previewImage: `https://via.placeholder.com/400x300/f59e0b/ffffff?text=Overlay`,
    galleryImages: [],
    gradient: 'from-amber-500 to-orange-700',
    examples: [
      'Wedding photo frame',
      'Social media overlay',
      'Holiday photo border',
      'Vintage photo effect',
    ],
    prompt: 'Design photo overlay',
    tips: [
      'Keep borders subtle',
      'Leave photo space clear',
      'Match photo style',
      'Consider transparency',
    ],
    metaTitle: 'Photo Overlay Generator - Photography Frames | SVG.new',
    metaDescription: 'Design decorative photo overlays and frames for photography.',
    keywords: ['photo overlay', 'photo frame', 'photography overlay', 'picture frame'],
  },
  {
    id: 'video-thumbnail',
    slug: 'video-thumbnail',
    title: 'Video Thumbnails',
    description: 'Eye-catching thumbnails for YouTube and video content',
    category: 'Web Design',
    icon: '🎬',
    previewImage: `https://via.placeholder.com/400x300/ef4444/ffffff?text=Thumbnail`,
    galleryImages: [],
    gradient: 'from-red-500 to-pink-600',
    examples: [
      'YouTube video thumbnail',
      'Tutorial video cover',
      'Vlog episode thumbnail',
      'Gaming video preview',
    ],
    prompt: 'Create video thumbnail',
    tips: ['Use bold text', 'Include facial expressions', 'Create curiosity', 'Test at small size'],
    metaTitle: 'Video Thumbnail Generator - YouTube Covers | SVG.new',
    metaDescription: 'Design compelling video thumbnails for YouTube and social media.',
    keywords: ['video thumbnail', 'youtube thumbnail', 'video cover', 'thumbnail design'],
  },
].filter(t => t.galleryImages.length > 0);

export const templateCategories = [
  'All',
  'Web Design',
  'Digital Art',
  'Art & Design',
  'UI/UX',
  // 'Fashion',
  'Branding',
  // 'Decorative',
  'Gaming',
  'Illustration',
  'Data Viz'
];

export function getTemplateBySlug(slug: string): Template | undefined {
  return templates.find(t => t.slug === slug);
}

export function getTemplateById(id: string): Template | undefined {
  return templates.find(t => t.id === id);
}

export function getTemplatesByCategory(category: string): Template[] {
  if (category === 'All') return templates;
  return templates.filter(t => t.category === category);
}